<script setup>
/**
 * Admin Dashboard View
 * Desktop-like interface where users can add and organize shortcuts
 * Device-aware grid system:
 * - Phone: 6 columns
 * - Tablet: 11 columns
 * - PC: Dynamic with visual zones
 */

import { ref, computed } from 'vue'
import { useDesktop } from '@/core/composables/useDesktop'
import DesktopToolbar from '@/ui/components/desktop/DesktopToolbar.vue'
import DesktopCanvas from '@/ui/components/desktop/DesktopCanvas.vue'
import ShortcutsModal from '@/ui/components/desktop/ShortcutsModal.vue'
import {
  WALLPAPERS,
  WALLPAPER_STORAGE_KEY,
  getWallpaperById,
} from '@/core/config/desktopWallpapers'

const {
  shortcutsList,
  isLoading,
  isModalOpen,
  availableShortcuts,
  addedShortcutIds,
  addShortcut,
  updatePosition,
  removeShortcut,
  openModal,
  closeModal,
  setDragging,
} = useDesktop()

function handleShortcutSelect(shortcut) {
  addShortcut(shortcut)
  closeModal()
}

function handleShortcutMove(shortcutId, position) {
  updatePosition(shortcutId, position)
}

function handleShortcutRemove(shortcutId) {
  removeShortcut(shortcutId)
}

function handleShortcutDragStart(shortcutId) {
  setDragging(shortcutId)
}

// Device zones visibility (persisted in localStorage)
const ZONES_STORAGE_KEY = 'desktop-show-zones'
const showZones = ref(localStorage.getItem(ZONES_STORAGE_KEY) !== 'false')

function toggleZones() {
  showZones.value = !showZones.value
  localStorage.setItem(ZONES_STORAGE_KEY, showZones.value)
}

// Settings modal
const isSettingsOpen = ref(false)

// Wallpaper selection
const OPACITY_KEY = 'desktop-wallpaper-opacity'
const FILL_KEY = 'desktop-wallpaper-fill'

const selectedWallpaperId = ref(localStorage.getItem(WALLPAPER_STORAGE_KEY) || 'default')
const wallpaperOpacity = ref(parseFloat(localStorage.getItem(OPACITY_KEY)) || 1)
const wallpaperFill = ref(localStorage.getItem(FILL_KEY) === 'true')

const activeWallpaper = computed(() => getWallpaperById(selectedWallpaperId.value))
const wallpaperStyle = computed(() => {
  const wp = activeWallpaper.value
  if (!wp.style) return null
  const base = { ...wp.style, opacity: wallpaperOpacity.value }
  if (wallpaperFill.value) {
    base.backgroundSize = 'cover'
    base.backgroundRepeat = 'no-repeat'
    base.backgroundPosition = 'center'
  }
  return base
})

function selectWallpaper(id) {
  selectedWallpaperId.value = id
  localStorage.setItem(WALLPAPER_STORAGE_KEY, id)
}

function setWallpaperOpacity(val) {
  wallpaperOpacity.value = parseFloat(val)
  localStorage.setItem(OPACITY_KEY, val)
}

function toggleWallpaperFill(mode) {
  wallpaperFill.value = mode === 'fill'
  localStorage.setItem(FILL_KEY, wallpaperFill.value)
}

// Accordion sections
const openSections = ref(new Set())
</script>

<template>
  <div class="admin-dashboard">
    <DesktopToolbar
      :show-zones="showZones"
      @open-start="openModal"
      @toggle-zones="toggleZones"
      @open-settings="isSettingsOpen = true"
    />

    <DesktopCanvas
      v-if="!isLoading"
      :shortcuts="shortcutsList"
      :show-zones="showZones"
      :wallpaper-style="wallpaperStyle"
      @shortcut-move="handleShortcutMove"
      @shortcut-remove="handleShortcutRemove"
      @shortcut-drag-start="handleShortcutDragStart"
    />

    <div v-else class="loading-state">
      <div class="loading-spinner"></div>
      <span class="loading-text">טוען שולחן עבודה...</span>
    </div>

    <ShortcutsModal
      :is-open="isModalOpen"
      :available-shortcuts="availableShortcuts"
      :added-shortcut-ids="addedShortcutIds"
      @close="closeModal"
      @select="handleShortcutSelect"
    />

    <!-- Settings Modal (placeholder) -->
    <Teleport to="body">
      <Transition name="modal">
        <div v-if="isSettingsOpen" class="settings-overlay" @click.self="isSettingsOpen = false">
          <div class="settings-modal">
            <div class="settings-header">
              <h2>הגדרות שולחן עבודה</h2>
              <button class="settings-close" @click="isSettingsOpen = false">
                <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
                  <path
                    d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"
                  />
                </svg>
              </button>
            </div>
            <div class="settings-body">
              <!-- Wallpaper Selection -->
              <div class="accordion-section">
                <button
                  class="accordion-header"
                  @click="
                    openSections.has('wallpaper')
                      ? openSections.delete('wallpaper')
                      : openSections.add('wallpaper')
                  "
                >
                  <svg
                    class="accordion-icon"
                    :class="{ open: openSections.has('wallpaper') }"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    width="18"
                    height="18"
                  >
                    <path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6z" />
                  </svg>
                  <span>רקע שולחן העבודה</span>
                </button>
                <div v-show="openSections.has('wallpaper')" class="accordion-body">
                  <div class="wallpaper-grid">
                    <button
                      v-for="wp in WALLPAPERS"
                      :key="wp.id"
                      class="wallpaper-thumb"
                      :class="{ active: selectedWallpaperId === wp.id }"
                      @click="selectWallpaper(wp.id)"
                    >
                      <div class="wallpaper-preview" :style="{ background: wp.previewBg }">
                        <svg
                          v-if="selectedWallpaperId === wp.id"
                          class="wallpaper-check"
                          viewBox="0 0 24 24"
                          fill="currentColor"
                        >
                          <path
                            d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"
                          />
                        </svg>
                      </div>
                      <span class="wallpaper-name">{{ wp.name }}</span>
                    </button>
                  </div>
                </div>
              </div>

              <!-- Wallpaper Display Options -->
              <div class="accordion-section">
                <button
                  class="accordion-header"
                  @click="
                    openSections.has('display')
                      ? openSections.delete('display')
                      : openSections.add('display')
                  "
                >
                  <svg
                    class="accordion-icon"
                    :class="{ open: openSections.has('display') }"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    width="18"
                    height="18"
                  >
                    <path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6z" />
                  </svg>
                  <span>הגדרות תצוגה</span>
                </button>
                <div v-show="openSections.has('display')" class="accordion-body">
                  <!-- Repeat / Fill -->
                  <div class="setting-row">
                    <label class="setting-label">מצב רקע</label>
                    <div class="toggle-group">
                      <button
                        class="toggle-btn"
                        :class="{ active: !wallpaperFill }"
                        @click="toggleWallpaperFill('repeat')"
                      >
                        חזרה
                      </button>
                      <button
                        class="toggle-btn"
                        :class="{ active: wallpaperFill }"
                        @click="toggleWallpaperFill('fill')"
                      >
                        מילוי
                      </button>
                    </div>
                  </div>

                  <!-- Opacity -->
                  <div class="setting-row">
                    <label class="setting-label">שקיפות</label>
                    <div class="slider-row">
                      <input
                        type="range"
                        min="0.1"
                        max="1"
                        step="0.05"
                        :value="wallpaperOpacity"
                        class="opacity-slider"
                        @input="setWallpaperOpacity($event.target.value)"
                      />
                      <span class="slider-value">{{ Math.round(wallpaperOpacity * 100) }}%</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<style scoped>
.admin-dashboard {
  display: flex;
  flex-direction: column;
  height: calc(100vh - var(--header-height));
  overflow: hidden;
}

.loading-state {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 50%, #e2e8f0 100%);
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 3px solid #e2e8f0;
  border-top-color: #3b82f6;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.loading-text {
  color: #64748b;
  font-size: 0.9rem;
}

/* Settings Modal */
.settings-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  backdrop-filter: blur(4px);
}

.settings-modal {
  background: white;
  border-radius: 12px;
  width: 90%;
  max-width: 480px;
  max-height: 85vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
  overflow: hidden;
}

.settings-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1.25rem;
  border-bottom: 1px solid #e2e8f0;
}

.settings-header h2 {
  margin: 0;
  font-size: 1.05rem;
  font-weight: 600;
  color: #1e293b;
}

.settings-close {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  background: none;
  color: #64748b;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.15s ease;
}

.settings-close:hover {
  background: #f1f5f9;
  color: #334155;
}

.settings-body {
  padding: 0.5rem 0;
  overflow-y: auto;
}

/* Accordion */
.accordion-section {
  border-bottom: 1px solid #f1f5f9;
}

.accordion-section:last-child {
  border-bottom: none;
}

.accordion-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
  padding: 0.75rem 1.25rem;
  border: none;
  background: none;
  cursor: pointer;
  font-size: 0.85rem;
  font-weight: 600;
  color: #475569;
  transition: background 0.15s ease;
}

.accordion-header:hover {
  background: #f8fafc;
}

.accordion-icon {
  transition: transform 0.2s ease;
  color: #94a3b8;
  flex-shrink: 0;
}

.accordion-icon.open {
  transform: rotate(180deg);
}

.accordion-body {
  padding: 0 1.25rem 1rem;
}

/* Setting rows */
.setting-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.6rem 0;
}

.setting-row + .setting-row {
  border-top: 1px solid #f1f5f9;
}

.setting-label {
  font-size: 0.8rem;
  color: #475569;
  font-weight: 500;
}

/* Toggle group */
.toggle-group {
  display: flex;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  overflow: hidden;
}

.toggle-btn {
  padding: 0.3rem 0.75rem;
  border: none;
  background: white;
  font-size: 0.75rem;
  color: #64748b;
  cursor: pointer;
  transition: all 0.15s ease;
}

.toggle-btn + .toggle-btn {
  border-right: 1px solid #e2e8f0;
}

.toggle-btn.active {
  background: #3b82f6;
  color: white;
}

/* Opacity slider */
.slider-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.opacity-slider {
  width: 100px;
  height: 4px;
  accent-color: #3b82f6;
  cursor: pointer;
}

.slider-value {
  font-size: 0.75rem;
  color: #64748b;
  min-width: 32px;
  text-align: center;
}

.wallpaper-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.75rem;
}

.wallpaper-thumb {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.4rem;
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;
}

.wallpaper-preview {
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 10;
  border-radius: 8px;
  border: 2px solid #e2e8f0;
  transition: all 0.15s ease;
}

.wallpaper-thumb:hover .wallpaper-preview {
  border-color: #94a3b8;
}

.wallpaper-thumb.active .wallpaper-preview {
  border-color: #3b82f6;
  box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.25);
}

.wallpaper-check {
  position: absolute;
  bottom: 4px;
  left: 4px;
  width: 20px;
  height: 20px;
  color: #3b82f6;
  background: white;
  border-radius: 50%;
}

.wallpaper-name {
  font-size: 0.75rem;
  color: #64748b;
  line-height: 1;
}

.wallpaper-thumb.active .wallpaper-name {
  color: #3b82f6;
  font-weight: 600;
}

/* Modal transition (matches ShortcutsModal) */
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.2s ease;
}

.modal-enter-active .settings-modal,
.modal-leave-active .settings-modal {
  transition: transform 0.2s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.modal-enter-from .settings-modal {
  transform: scale(0.95);
}

.modal-leave-to .settings-modal {
  transform: scale(0.95);
}
</style>
